<template>
    <div class="pa-5 compact-form" v-if="activeShipment">
        <v-row no-gutters>
            <v-col class="mt-2 row-title" cols="3">{{ "Direction" | t }}</v-col>
            <v-col cols="6">
                <v-select
                        :value="activeShipment.direction"
                        @input="updateField($event, 'direction')"
                        :items="directionOptions"
                        class="text-field-item mb-1"
                        dense
                        item-text="text"
                        item-value="value"
                        hide-details
                        outlined
                />
            </v-col>
        </v-row>
        <v-row no-gutters>
            <v-col class="mt-2 row-title" cols="3">{{ "Company name" | t }}</v-col>
            <v-col cols="6">
                <v-text-field
                        :value="activeShipment.company_name"
                        @input="updateField($event, 'company_name')"
                        class="text-field-item mb-1"
                        hide-details
                        dense
                        outlined
                />
            </v-col>
        </v-row>
        <v-row no-gutters>
            <v-col class="mt-2 row-title" cols="3">{{ "Car nr" | t }}</v-col>
            <v-col cols="6">
                <v-text-field
                        :value="activeShipment.vehicle_reg_number"
                        @input="updateField($event, 'vehicle_reg_number')"
                        class="text-field-item mb-1"
                        hide-details
                        dense
                        outlined
                />
            </v-col>
        </v-row>
        <v-row no-gutters>
            <v-col class="mt-2 row-title" cols="3">{{ "Shipment size" | t }}</v-col>
            <v-col cols="6">
                <v-autocomplete
                        :value="activeShipment.shipment_size"
                        :items="sizeOptions"
                        class="mb-1"
                        outlined
                        clearable
                        dense
                        hide-details
                        hide-no-data
                        hide-selected
                        :item-text="item => `${t(item.text)}`"
                        return-object
                        @change="updateField($event, 'shipment_size')"
                />
            </v-col>
        </v-row>
        <v-row no-gutters>
            <v-col class="mt-2 row-title" cols="3">{{ "Comment" | t }}</v-col>
            <v-col cols="6">
                <v-text-field
                        :value="activeShipment.comment"
                        class="text-field-item mb-1"
                        dense
                        hide-details
                        outlined
                        @input="updateField($event, 'comment')"
                />
            </v-col>
        </v-row>

        <v-row no-gutters>
            <v-col class="mt-2">
                <vuecal
                        :locale="vuecalLocale"
                        :key="vuecalKey"
                        :events="events"
                        :disable-views="['years', 'year', 'day']"
                        hide-view-selector
                        show-week-numbers
                        hide-weekends
                        :snapToTime="snapToTime"
                        :time-from="timeFrom"
                        :time-to="timeToDisplay"
                        :time-step="30"
                        :time-cell-height="50"
                        :min-date="minDate"
                        :cell-click-hold="false"
                        :drag-to-create-event="false"
                        :events-on-month-view="'short'"
                        :editable-events="{ title: false, drag: true, resize: false, delete: true, create: false }"
                        :disable-days=disabledDates
                        ref="vuecal"
                        @cell-click="onEventCreate"
                        @event-delete="setCreatedEvent(null)"
                        @ready="onReady"
                >
                    <template #event="{ event, view }">
                        <!-- Week View -->
                        <div :class="[
                              'existing-event',
                              event.id === activeShipment.id ? 'current-event' : `status-${event.status}`
                            ]"
                             v-if="view === 'week'">
                            <div class="vuecal__event-title pb-2 font-weight-bold company-name"
                                 v-html="event.title"> </div>
                            <small class="vuecal__event-time">
                                <strong>{{ "a" + event.id }} {{ "n" +activeShipment.id }} {{ `${ t("Car nr") }:` }}</strong> <span>{{
                                    event.vehicle_reg_number
                                }}</span><br/>
                                <strong>{{ `${ t("Start") }:` }}</strong> <span>{{
                                    event.start.formatTime("HH:mm")
                                }}</span><br/>
                                <strong>{{ `${ t("End") }:` }}</strong> <span>{{ event.end.formatTime("HH:mm") }}</span>
                            </small>
                        </div>

                        <!-- Month View -->
                        <div class="month-event"
                             :class="[
                              'month-event',
                              event.id === activeShipment.id ? 'current-event' : `status-month-${event.status}`
                            ]"
                             v-else
                        >
                            <div>
                                <span class="company-name">{{ event.title }}: </span>
                                <span class="event-times">{{
                                        event.start.formatTime("HH:mm")
                                    }}-{{ event.end.formatTime("HH:mm") }}</span>
                            </div>
                        </div>
                    </template>

                </vuecal>
            </v-col>
        </v-row>

    </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";
import moment from "moment";
import { helperMixin } from "@/mixins/helper-mixin";
import VueCal from 'vue-cal'
import 'vue-cal/dist/vuecal.css'
import holidays from './data-item/public-holidays.json';

export default {
    name: "general-edit-tab",
    mixins: [helperMixin],
    components: { vuecal: VueCal },
    data() {
        return {
            events: [],
            vuecalKey: 0,
            snapToTime: 15,
            timeFrom: 480,
            timeTo: 930,
            timeToDisplay: 960,
            directionOptions: [
                {
                    text: this.t("Outgoing"),
                    value: "outgoing"
                },
                {
                    text: this.t("Incoming"),
                    value: "incoming"
                }
            ],

        }
    },
    computed: {
        ...mapState(["user"]),
        ...mapState("ShipmentStore", ["shipments", "activeShipment", "errorMessage", "loadingShipmentData", "createdEvent", "sizes", "vuecalLocale"]),
        sizeOptions() {
            return this.activeShipment.direction ? this.sizes[this.activeShipment.direction] : [];
        },
        minDate() {
            return new Date()
        },
        disabledDates() {
            return Object.values(holidays).flatMap(year => Object.values(year));
        },
        fieldsFilled() {
            const { direction, company_name, vehicle_reg_number, shipment_size } = this.activeShipment;
            return direction && company_name && vehicle_reg_number && shipment_size?.id;
        },
    },
    async mounted() {
        await new Promise(resolve => setTimeout(resolve, 300));

        if (!this.shipments) {
            await this.getShipmentList()
        }

        this.events = this.shipments[this.activeShipment.direction] || []

        if (this.activeShipment.id) {
            const direction = this.activeShipment.direction;
            const sizeList = this.sizes[direction] || [];
            const sizeMatch = sizeList.find(size => size.id === this.activeShipment.shipment_size);

            if (sizeMatch) {
                this.updateField(sizeMatch, "shipment_size");
            }
            let events = this.events || [];

            const eventIndex = events.findIndex(e => e.id === this.activeShipment.id);

            if (eventIndex !== -1) {

                this.removeActiveShipmentFromShipments(this.activeShipment.id)
                await new Promise(resolve => setTimeout(resolve, 100));
                const createdEvent = await this.$refs.vuecal.createEvent(
                        this.activeShipment.start,
                        this.activeShipment.shipment_size.value,
                        {
                            title: this.activeShipment.company_name,
                            content: this.activeShipment.vehicle_reg_number,
                            class: 'new-event',
                            status: this.activeShipment.status,
                            id: this.activeShipment.id
                        }
                );
                this.setCreatedEvent(createdEvent);
            }
        }
    },
    watch: {
        fieldsFilled(newVal) {
            if (newVal && this.$route.params.shipmentId === "new") {
                this.createEventInFirstAvailableSlot();
            }
        },
        'activeShipment.direction': async function (newValue) {
            this.events = this.shipments[this.activeShipment.direction] || []
            if (this.createdEvent && this.$route.params.shipmentId === "new") {
                await this.deleteEvent();
                await this.createEventInFirstAvailableSlot();
            }
        },
        'activeShipment.shipment_size': async function (newValue) {
            if (this.createdEvent && this.$route.params.shipmentId === "new") {
                await this.deleteEvent();
                await this.createEventInFirstAvailableSlot();
            }
        },
    },
    methods: {
        ...mapMutations("ShipmentStore", ["updateActiveShipmentProperty", "setCreatedEvent", "setRequestError", "setErrorMessage", "setShipments"]),
        ...mapActions("ShipmentStore", ["getShipmentData", "getShipmentList"]),
        ...mapActions(["getFrequentlyUsedQueries"]),
        updateField(value, field) {
            this.updateActiveShipmentProperty({ prop: field, value: value });
        },
        resetCalendarView() {
            const currentWeekNumber = moment().isoWeek();
            this.$refs.vuecal.switchView('week', currentWeekNumber);
        },
        async createEventInFirstAvailableSlot() {

            if (!this.activeShipment?.shipment_size?.value) {
                return;
            }

            let momentTime = moment();
            const snapTo = this.snapToTime || 15;
            const timeTo = this.timeTo;
            const shipmentSize = this.activeShipment.shipment_size.value;
            const timeFrom = this.timeFrom;


            const minutes = momentTime.minute();
            const roundedMinutes = Math.round(minutes / snapTo) * snapTo;
            momentTime = momentTime.minute(roundedMinutes).second(0);

            let weekdayIndex = momentTime.isoWeekday();
            let availableTimeSlots = await this.getAvailableTimeSlots(weekdayIndex);

            let counter = 0;
            while (true) {
                if (counter >= 150) {
                    console.warn("Could not find a timeslot, breaking.");
                    break;
                }
                counter++;

                const formattedDate = momentTime.format('YYYY-MM-DD');

                const isDisabledDate = this.disabledDates.includes(formattedDate);
                if (isDisabledDate) {
                    momentTime.add(1, 'day').startOf('day').minute(timeFrom);
                    continue;
                }

                if (await this.beforeCreateCheck(momentTime, availableTimeSlots)) {
                    break;
                }

                const currentMinutes = momentTime.hour() * 60 + momentTime.minute();

                if (currentMinutes >= timeTo - shipmentSize) {
                    weekdayIndex++;

                    if (weekdayIndex > 5) {
                        weekdayIndex = 1;
                        this.$refs.vuecal.next();
                        momentTime.add(1, 'week');
                    }

                    momentTime = momentTime.isoWeekday(weekdayIndex).startOf('day').minute(timeFrom);

                    if (momentTime.isBefore(moment())) {
                        momentTime.add(1, 'week');
                    }

                    availableTimeSlots = await this.getAvailableTimeSlots(weekdayIndex);
                }
                momentTime.add(snapTo, 'minutes');
            }

            await this.onEventCreate(momentTime.format('YYYY-MM-DD HH:mm'));
        },


        async onEventCreate(event) {
            if (this.createdEvent) {
                return false;
            }
            if (this.$refs.vuecal.view.id == 'month') return false;
            if (!this.fieldsFilled) {
                this.setRequestError(true)
                this.setErrorMessage(this.t("Please, fill all the fields"))
                return false;
            }

            let momentTime = moment(event, 'YYYY-MM-DD HH:mm');
            const snapTo = this.snapToTime || 15;
            const minutes = momentTime.minute();
            const roundedMinutes = Math.round(minutes / snapTo) * snapTo;
            momentTime = momentTime.minute(roundedMinutes).second(0)

            let weekdayIndex = momentTime.isoWeekday();
            const availableTimeSlots = await this.getAvailableTimeSlots(weekdayIndex);
            const beforeCreateCheck = this.beforeCreateCheck(momentTime, availableTimeSlots)

            if (!beforeCreateCheck) {
                this.setRequestError(true)
                this.setErrorMessage("Timeslot is unavailable, please pick another")
                return false;
            }

            const createdEvent = this.$refs.vuecal.createEvent(
                    momentTime.format('YYYY-MM-DD HH:mm'),
                    this.activeShipment.shipment_size.value,
                    {
                        title: this.activeShipment.company_name,
                        content: this.activeShipment.vehicle_reg_number,
                        class: 'new-event'
                    }
            );
            this.setCreatedEvent(createdEvent)
            return true
        },
        beforeCreateCheck(momentTime, availableTimeSlots) {
            const startTimeMinutes = momentTime.hour() * 60 + momentTime.minute();
            const durationInMinutes = this.activeShipment.shipment_size.value;
            const wholeDay = this.timeTo - this.timeFrom < durationInMinutes;
            const endTimeMinutes = startTimeMinutes + durationInMinutes;

            if (wholeDay) {
                return Object.values(availableTimeSlots).every(slot => slot === 1);
            }

            if (endTimeMinutes > this.timeTo) return false;

            for (let time = startTimeMinutes; time < endTimeMinutes; time += this.snapToTime) {
                if (availableTimeSlots[time] === 0) {
                    return false;
                }
            }
            return true;
        },
        async getAvailableTimeSlots(weekdayIndex) {
            const weekday = this.$refs.vuecal.$children[weekdayIndex];

            if (!weekday) return {};
            const availableSlots = {};
            for (let time = this.timeFrom; time <= this.timeTo; time += this.snapToTime) {
                availableSlots[time] = 1;
            }

            weekday.events.forEach(event => {
                const start = event.startTimeMinutes;
                const end = event.endTimeMinutes;

                for (let time = start; time < end; time += this.snapToTime) {
                    if (availableSlots[time] !== undefined) {
                        availableSlots[time] = 0;
                    }
                }
            });

            return availableSlots;
        },
        removeActiveShipmentFromShipments(shipmentId) {
            this.events = this.events.filter(event => event.id !== shipmentId);
        },
        deleteEvent() {
            this.setCreatedEvent(null)
            this.resetCalendarView()
            this.vuecalKey++
        },
        onReady() {
            setTimeout(async () => {
                this.$refs.vuecal.switchView('week', this.activeShipment.start);
            }, 300);
        },
    }
};
</script>

<style scoped>

.existing-event {
    border-radius: 6px;
    width: 98.5%;
    height: 99%;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
    cursor: pointer;
}

.month-event {
    text-align: left;
    padding-left: 0.3em;
    border-radius: 4px;
    width: 98.5%;
    height: 98%;
    margin-bottom: 2px;
    box-shadow: 1px 1px 3px rgba(0, 0, 0, 0.4);
}

.current-event {
    background: rgba(252, 37, 37, 0.15) !important;
}

.current-event-month {
    background: rgba(252, 37, 37, 0.15) !important;
}

.status-pending {
    background: rgba(252, 37, 37, 0.15);
}

.status-waiting {
    background: rgba(0, 123, 255, 0.15);
}

.status-done {
    background: rgba(40, 167, 69, 0.15);
}

.status-month-pending {
    background: rgba(252, 37, 37, 0.15);
}

.status-month-waiting {
    background: rgba(0, 123, 255, 0.15);
}

.status-month-done {
    background: rgba(40, 167, 69, 0.15);
}

.row-title {
    color: #888;
}

.new-event {
    background: rgba(252, 37, 37, 0.15);
    border-radius: 6px;
    width: 98.5%;
    height: 99%;
    box-shadow: 1px 1px 3px rgba(0, 0, 0, 0.4);
    cursor: pointer;
}

.event-times {
    font-size: 0.9em;
    color: #555;
}

.vuecal__event-title {
    font-size: 14px;
    font-weight: bold;
    color: #333;
}

.vuecal__event-time {
    font-size: 12px;
    color: #666;
}

.company-name {
    font-weight: bold;
    color: #3d3d3d;
}

.event-times {
    font-size: 0.9em;
    color: #555;
}

.vuecal__event-title {
    font-size: 14px;
    font-weight: bold;
    color: #333;
}

.vuecal__event-time {
    font-size: 12px;
    color: #666;
}
</style>
